import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useSocket } from "@/hooks/useSocket";
import { getBrowserCapabilities, getGeolocationOrNull, safeGetUserMedia } from "@/lib/browserCapabilities";
import { trpc } from "@/lib/trpc";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Shield, Camera, QrCode, AlertTriangle, CheckCircle2,
  MapPin, Phone, Car, UtensilsCrossed, ScanFace,
  Vibrate, ChevronRight, User, Clock, Bell,
  Megaphone, X as XIcon, Loader2, Home, Menu,
  FileWarning, Send, Image as ImageIcon, ArrowLeft, UserPlus, MapPinned
} from "lucide-react";

const LOGO_URL = "https://d2xsxph8kpxj0f.cloudfront.net/310419663030788589/ZZQT68UYN6EHt44KWwBWmG/ella_morumbis_logo_5b24d8ff.png";

type AppScreen = "home" | "emergency" | "qr_scan" | "checkin" | "report_aggressor" | "register_aggressor" | "services" | "info" | "my_events" | "trip";

const PRESENCE_STORAGE_KEY = "ella:presence-id:v1";

function getPersistentPresenceId() {
  try {
    const existingId = window.localStorage.getItem(PRESENCE_STORAGE_KEY);
    if (existingId) return existingId;
    const generatedId = typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    window.localStorage.setItem(PRESENCE_STORAGE_KEY, generatedId);
    return generatedId;
  } catch {
    return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  }
}

let faceapi: any = null;

export default function MobileApp() {
  const { socket, connected: socketConnected } = useSocket();
  const [screen, setScreen] = useState<AppScreen>("home");
  const [userName, setUserName] = useState("");
  const [userCheckedIn, setUserCheckedIn] = useState(false);
  const [userSector, setUserSector] = useState("");
  const [pendingEvents, setPendingEvents] = useState<any[]>([]);
  const [showMenu, setShowMenu] = useState(false);
  const [shakeEnabled, setShakeEnabled] = useState(true);
  const [presenceId] = useState(getPersistentPresenceId);
  const [operatorCameraRequest, setOperatorCameraRequest] = useState(0);
  const [centralMessage, setCentralMessage] = useState<string | null>(null);
  const [voiceAlertsEnabled, setVoiceAlertsEnabled] = useState(false);

  // V10: Read QR code from URL param to identify environment
  const qrCodeParam = useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('qr') || null;
  }, []);
  const sessionCodeParam = useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    const direct = params.get('session');
    if (direct) return direct;
    const match = window.location.pathname.match(/^\/connect\/([^/]+)/);
    return match?.[1] ? decodeURIComponent(match[1]) : null;
  }, []);
  const qrInfo = trpc.namedQr.getByCode.useQuery(
    { code: qrCodeParam || '' },
    { enabled: !!qrCodeParam }
  );
  const sessionInfo = trpc.session.getByCode.useQuery(
    { code: sessionCodeParam || '' },
    { enabled: !!sessionCodeParam, retry: 1 }
  );

  // A QR de sessão deve sempre desembarcar no fluxo de check-in do aplicativo.
  useEffect(() => {
    if (!sessionCodeParam) return;
    if (sessionInfo.isSuccess && sessionInfo.data) {
      setUserSector(prev => prev || `Sessão ${sessionCodeParam}`);
      setScreen(prev => prev === "home" ? "checkin" : prev);
    } else if (sessionInfo.isError) {
      toast.error("Sessão inválida ou expirada. Solicite um novo QR à Central.");
    }
  }, [sessionCodeParam, sessionInfo.isSuccess, sessionInfo.isError, sessionInfo.data]);


  // Bind the mobile socket to the QR-created session before publishing presence.
  useEffect(() => {
    if (!socket || !socketConnected || !sessionCodeParam || !sessionInfo.data) return;
    socket.emit("device:join", {
      sessionCode: sessionCodeParam,
      deviceName: navigator.platform || "Dispositivo móvel",
      deviceBrowser: navigator.userAgent,
    });
  }, [socket, socketConnected, sessionCodeParam, sessionInfo.data]);

  // Publish a stable app identity as soon as the socket connects.
  useEffect(() => {
    if (!socket || !socketConnected) return;
    const publishPresence = (location: { lat: number; lng: number } | null) => {
      socket.emit("mobile:presence", {
        clientId: presenceId,
        name: userName || "Usuária no App",
        sector: userSector || qrInfo.data?.location || qrInfo.data?.name || (sessionCodeParam ? `Sessão ${sessionCodeParam}` : "Não informado"),
        location,
        sessionCode: sessionCodeParam || undefined,
      });
    };
    const geo = getGeolocationOrNull();
    if (!geo) {
      publishPresence(null);
      return;
    }
    geo.getCurrentPosition(
      position => publishPresence({ lat: position.coords.latitude, lng: position.coords.longitude }),
      () => publishPresence(null),
      { enableHighAccuracy: true, maximumAge: 10000, timeout: 15000 }
    );
  }, [socket, socketConnected, presenceId, userName, userSector, qrInfo.data?.location, qrInfo.data?.name, sessionCodeParam]);

  useEffect(() => {
    if (!socket) return;
    const onCameraRequest = () => {
      socket.emit("mobile:camera-request-ack", { clientId: presenceId, status: "received" });
      setOperatorCameraRequest(Date.now());
      setScreen("emergency");
      toast.info("A Central solicitou sua câmera ao vivo.");
      if (navigator.vibrate) navigator.vibrate([150, 80, 150]);
    };
    socket.on("dashboard:camera-request", onCameraRequest);
    return () => { socket.off("dashboard:camera-request", onCameraRequest); };
  }, [socket, presenceId]);

  useEffect(() => {
    if (!socket) return;
    const onCentralMessage = (data: { message?: string }) => {
      const message = data.message?.trim();
      if (!message) return;
      setCentralMessage(message);
      socket.emit("mobile:central-message-ack", { clientId: presenceId });
      toast.info(`Central: ${message}`, { duration: 12000 });
      if (navigator.vibrate) navigator.vibrate([120, 80, 120]);
    };
    socket.on("central:message-to-app", onCentralMessage);
    return () => { socket.off("central:message-to-app", onCentralMessage); };
  }, [socket, presenceId]);

  useEffect(() => {
    if (!voiceAlertsEnabled || !centralMessage || !window.speechSynthesis) return;
    window.speechSynthesis.cancel();
    const utterance = new SpeechSynthesisUtterance(`Mensagem da Central: ${centralMessage}`);
    utterance.lang = "pt-BR";
    utterance.rate = 0.95;
    window.speechSynthesis.speak(utterance);
    return () => window.speechSynthesis.cancel();
  }, [voiceAlertsEnabled, centralMessage]);

  // Shake detection for emergency activation
  const [shakePermission, setShakePermission] = useState<'granted' | 'denied' | 'pending'>('pending');

  // Request motion permission on iOS 13+
  useEffect(() => {
    const DME = typeof window !== "undefined" ? (window as any).DeviceMotionEvent : undefined;
    if (DME && typeof DME.requestPermission === 'function') {
      // iOS 13+ requires explicit permission
      setShakePermission('pending');
    } else {
      // Android / older iOS — permission not needed
      setShakePermission('granted');
    }
  }, []);

  const requestShakePermission = useCallback(async () => {
    const DME = typeof window !== "undefined" ? (window as any).DeviceMotionEvent : undefined;
    if (DME && typeof DME.requestPermission === 'function') {
      try {
        const result = await DME.requestPermission();
        setShakePermission(result === 'granted' ? 'granted' : 'denied');
        if (result === 'granted') toast.success('Detecção de movimento ativada!');
        else toast.error('Permissão de movimento negada');
      } catch {
        setShakePermission('denied');
      }
    } else {
      setShakePermission('granted');
    }
  }, []);

  useEffect(() => {
    if (!shakeEnabled || shakePermission !== 'granted') return;
    let lastX = 0, lastY = 0, lastZ = 0;
    let lastTime = 0;
    let lastShakeTime = 0;
    const SHAKE_THRESHOLD = 25;
    const SHAKE_COOLDOWN = 5000; // 5s cooldown between shakes

    const handleMotion = (e: DeviceMotionEvent) => {
      const acc = e.accelerationIncludingGravity;
      if (!acc || acc.x === null || acc.y === null || acc.z === null) return;
      const now = Date.now();
      if (now - lastTime < 100) return; // throttle
      const deltaX = Math.abs(acc.x - lastX);
      const deltaY = Math.abs(acc.y - lastY);
      const deltaZ = Math.abs(acc.z - lastZ);
      if (deltaX + deltaY + deltaZ > SHAKE_THRESHOLD && now - lastShakeTime > SHAKE_COOLDOWN) {
        lastShakeTime = now;
        setScreen("emergency");
        toast.info("Emergência ativada por movimento!", { icon: "🚨" });
        // Vibrate if available
        if (navigator.vibrate) navigator.vibrate([200, 100, 200]);
      }
      lastX = acc.x; lastY = acc.y; lastZ = acc.z;
      lastTime = now;
    };

    window.addEventListener("devicemotion", handleMotion);
    return () => window.removeEventListener("devicemotion", handleMotion);
  }, [shakeEnabled, shakePermission]);

  return (
    <div className="min-h-screen bg-[#0a0a12] text-white flex flex-col" style={{ maxWidth: "100vw", overflow: "hidden" }}>
      {/* Header */}
      <header className="sticky top-0 z-50 bg-[#0a0a12]/95 backdrop-blur-md border-b border-purple-500/20 px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          {screen !== "home" && (
            <button onClick={() => setScreen("home")} className="p-1 rounded hover:bg-white/10 cursor-pointer">
              <ArrowLeft className="w-5 h-5 text-purple-400" />
            </button>
          )}
          <img src={LOGO_URL} alt="ELLA" className="h-8 object-contain" />
        </div>
        <div className="flex items-center gap-2">
          <div className={`w-2 h-2 rounded-full ${socketConnected ? "bg-green-400" : "bg-red-400"} animate-pulse`} />
          <button onClick={() => setShowMenu(!showMenu)} className="p-1.5 rounded hover:bg-white/10 cursor-pointer">
            <Menu className="w-5 h-5 text-white/70" />
          </button>
        </div>
      </header>

      {/* Menu overlay */}
      {showMenu && (
        <div className="fixed inset-0 z-40 bg-black/60 backdrop-blur-sm" onClick={() => setShowMenu(false)}>
          <div className="absolute right-0 top-0 bottom-0 w-72 bg-[#12121e] border-l border-purple-500/20 p-6" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-bold text-white">Menu</h2>
              <button onClick={() => setShowMenu(false)} className="p-1 rounded hover:bg-white/10 cursor-pointer">
                <XIcon className="w-5 h-5" />
              </button>
            </div>
            <nav className="space-y-1">
              {[
                { id: "home" as AppScreen, label: "Início", icon: Home },
                { id: "emergency" as AppScreen, label: "Emergência", icon: AlertTriangle },
                { id: "trip" as AppScreen, label: "Trajeto Protegido", icon: MapPinned },
                { id: "qr_scan" as AppScreen, label: "Escanear QR Code", icon: QrCode },
                { id: "checkin" as AppScreen, label: "Check-in Facial", icon: ScanFace },
                { id: "report_aggressor" as AppScreen, label: "Reportar Agressor", icon: FileWarning },
                { id: "register_aggressor" as AppScreen, label: "Pré-cadastrar Agressor", icon: UserPlus },
                { id: "services" as AppScreen, label: "Serviços", icon: Car },
                { id: "info" as AppScreen, label: "Informações", icon: Megaphone },
                { id: "my_events" as AppScreen, label: "Meus Chamados", icon: Bell },
              ].map(item => (
                <button
                  key={item.id}
                  onClick={() => { setScreen(item.id); setShowMenu(false); }}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg cursor-pointer transition-colors ${screen === item.id ? "bg-purple-500/20 text-purple-300" : "text-white/70 hover:bg-white/5"}`}
                >
                  <item.icon className="w-5 h-5" />
                  <span className="text-sm font-medium">{item.label}</span>
                </button>
              ))}
            </nav>
            <div className="mt-8 p-3 rounded-lg bg-purple-500/10 border border-purple-500/20">
              <p className="text-[10px] text-purple-300/60 uppercase tracking-wider mb-1">Chacoalhar = Emergência</p>
              <div className="flex items-center justify-between">
                <span className="text-xs text-white/70">Ativação por movimento</span>
                <button
                  onClick={() => setShakeEnabled(!shakeEnabled)}
                  className={`w-10 h-5 rounded-full transition-colors cursor-pointer ${shakeEnabled ? "bg-purple-500" : "bg-white/20"}`}
                >
                  <div className={`w-4 h-4 rounded-full bg-white transition-transform ${shakeEnabled ? "translate-x-5" : "translate-x-0.5"}`} />
                </button>
              </div>
              {shakePermission === 'pending' && (
                <button
                  onClick={requestShakePermission}
                  className="mt-2 w-full text-xs py-2 rounded-lg bg-purple-600 text-white cursor-pointer hover:bg-purple-700"
                >
                  Ativar Detecção de Movimento
                </button>
              )}
              {shakePermission === 'denied' && (
                <p className="mt-2 text-[10px] text-red-400">Permissão negada. Recarregue a página para tentar novamente.</p>
              )}
            </div>
            <div className="mt-4 p-3 rounded-lg bg-cyan-500/10 border border-cyan-500/20">
              <div className="flex items-center justify-between">
                <span className="text-xs text-white/70">Narração de mensagens</span>
                <button onClick={() => setVoiceAlertsEnabled(!voiceAlertsEnabled)} className={`w-10 h-5 rounded-full transition-colors cursor-pointer ${voiceAlertsEnabled ? "bg-cyan-500" : "bg-white/20"}`} aria-label="Alternar narração">
                  <div className={`w-4 h-4 rounded-full bg-white transition-transform ${voiceAlertsEnabled ? "translate-x-5" : "translate-x-0.5"}`} />
                </button>
              </div>
              <p className="mt-1 text-[10px] text-cyan-200/60">Opcional; usa a voz do navegador para mensagens da Central.</p>
            </div>
          </div>
        </div>
      )}

      {centralMessage && (
        <div className="mx-4 mt-3 p-3 rounded-xl bg-pink-500/15 border border-pink-400/30 text-pink-100 text-sm flex items-start gap-2">
          <Bell className="w-4 h-4 mt-0.5 shrink-0" />
          <div className="flex-1"><strong className="text-xs uppercase tracking-wide">Mensagem da Central</strong><p className="mt-1">{centralMessage}</p></div>
          <button onClick={() => setCentralMessage(null)} className="text-pink-200/60 hover:text-pink-100 cursor-pointer" aria-label="Fechar mensagem"><XIcon className="w-4 h-4" /></button>
        </div>
      )}

      {/* Content */}
      <main className="flex-1 overflow-y-auto">
        {screen === "home" && (
          <HomeScreen
            setScreen={setScreen}
            userCheckedIn={userCheckedIn}
            userName={userName}
            userSector={userSector}
            pendingEvents={pendingEvents}
          />
        )}
        {screen === "emergency" && (
          <EmergencyScreen
            socket={socket}
            socketConnected={socketConnected}
            presenceId={presenceId}
            userSector={userSector}
            userName={userName}
            setScreen={setScreen}
            setPendingEvents={setPendingEvents}
            qrEnvironment={qrInfo.data ? `${qrInfo.data.name}${qrInfo.data.location ? ' - ' + qrInfo.data.location : ''}` : undefined}
            qrCode={qrCodeParam || undefined}
            qrSector={qrInfo.data?.location || undefined}
            operatorCameraRequest={operatorCameraRequest}
          />
        )}
        {screen === "qr_scan" && (
          <QRScanScreen
            socket={socket}
            socketConnected={socketConnected}
            setScreen={setScreen}
          />
        )}
        {screen === "checkin" && (
          <CheckinScreen
            socket={socket}
            socketConnected={socketConnected}
            presenceId={presenceId}
            setUserName={setUserName}
            setUserCheckedIn={setUserCheckedIn}
            setUserSector={setUserSector}
            setScreen={setScreen}
          />
        )}
        {screen === "trip" && (
          <TripScreen
            socket={socket}
            socketConnected={socketConnected}
            userName={userName}
            presenceId={presenceId}
            sessionCode={sessionCodeParam}
            setScreen={setScreen}
          />
        )}
        {screen === "report_aggressor" && (
          <ReportAggressorScreen
            socket={socket}
            socketConnected={socketConnected}
            userName={userName}
            userSector={userSector}
            setScreen={setScreen}
          />
        )}
        {screen === "register_aggressor" && (
          <RegisterAggressorScreen
            setScreen={setScreen}
          />
        )}
        {screen === "services" && <ServicesScreen />}
        {screen === "info" && <InfoScreen />}
        {screen === "my_events" && <MyEventsScreen events={pendingEvents} setScreen={setScreen} />}
      </main>

      {/* Bottom Navigation */}
      <nav className="sticky bottom-0 bg-[#0a0a12]/95 backdrop-blur-md border-t border-purple-500/20 px-2 py-2 flex items-center justify-around">
        {[
          { id: "home" as AppScreen, label: "Início", icon: Home },
          { id: "qr_scan" as AppScreen, label: "QR Code", icon: QrCode },
          { id: "emergency" as AppScreen, label: "SOS", icon: AlertTriangle, highlight: true },
          { id: "services" as AppScreen, label: "Serviços", icon: Car },
          { id: "my_events" as AppScreen, label: "Chamados", icon: Bell },
        ].map(item => (
          <button
            key={item.id}
            onClick={() => setScreen(item.id)}
            className={`flex flex-col items-center gap-0.5 px-3 py-1.5 rounded-lg cursor-pointer transition-colors ${
              item.highlight
                ? "bg-red-500/20 border border-red-500/30"
                : screen === item.id
                  ? "text-purple-400"
                  : "text-white/40 hover:text-white/60"
            }`}
          >
            <item.icon className={`w-5 h-5 ${item.highlight ? "text-red-400" : ""}`} />
            <span className={`text-[9px] font-medium ${item.highlight ? "text-red-300" : ""}`}>{item.label}</span>
          </button>
        ))}
      </nav>

      {/* Advertising banner */}
      <div className="bg-gradient-to-r from-purple-900/40 via-purple-800/30 to-purple-900/40 border-t border-purple-500/10 px-4 py-2 text-center">
        <p className="text-[9px] text-purple-300/50 uppercase tracking-widest">Espaço Publicitário</p>
        <p className="text-[10px] text-white/40 mt-0.5">Sua marca aqui — Patrocine a segurança no estádio</p>
      </div>
    </div>
  );
}

/* ─── HOME SCREEN ─── */
function HomeScreen({ setScreen, userCheckedIn, userName, userSector, pendingEvents }: {
  setScreen: (s: AppScreen) => void;
  userCheckedIn: boolean;
  userName: string;
  userSector: string;
  pendingEvents: any[];
}) {
  return (
    <div className="px-4 py-6 space-y-6">
      {/* Welcome / Check-in status */}
      {userCheckedIn ? (
        <div className="p-4 rounded-xl bg-gradient-to-br from-green-500/10 to-green-500/5 border border-green-500/20">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-green-500/20 flex items-center justify-center">
              <CheckCircle2 className="w-5 h-5 text-green-400" />
            </div>
            <div>
              <p className="text-sm font-semibold text-green-300">Check-in realizado</p>
              <p className="text-xs text-white/60">{userName} — {userSector}</p>
            </div>
          </div>
        </div>
      ) : (
        <div className="p-4 rounded-xl bg-gradient-to-br from-purple-500/10 to-purple-500/5 border border-purple-500/20">
          <p className="text-sm font-semibold text-purple-300 mb-1">Bem-vindo ao estádio!</p>
          <p className="text-xs text-white/50 mb-3">Faça seu check-in facial para uma experiência completa</p>
          <Button onClick={() => setScreen("checkin")} className="w-full bg-purple-600 hover:bg-purple-700 text-white cursor-pointer">
            <ScanFace className="w-4 h-4 mr-2" /> Fazer Check-in
          </Button>
        </div>
      )}

      {/* Emergency SOS button */}
      <button
        onClick={() => {
          // Request shake permission on first SOS tap (iOS)
          const DME = typeof window !== "undefined" ? (window as any).DeviceMotionEvent : undefined;
          if (DME && typeof DME.requestPermission === 'function') {
            DME.requestPermission().catch(() => {});
          }
          setScreen("emergency");
        }}
        className="w-full p-5 rounded-xl bg-gradient-to-br from-red-600/20 to-red-800/10 border-2 border-red-500/40 flex items-center gap-4 cursor-pointer hover:border-red-500/60 transition-colors active:scale-[0.98]"
      >
        <div className="w-14 h-14 rounded-full bg-red-500/20 flex items-center justify-center animate-pulse">
          <AlertTriangle className="w-7 h-7 text-red-400" />
        </div>
        <div className="flex-1 text-left">
          <p className="text-lg font-bold text-red-300">EMERGÊNCIA</p>
          <p className="text-xs text-white/50">Toque ou chacoalhe o celular para acionar</p>
        </div>
        <ChevronRight className="w-5 h-5 text-red-400/50" />
      </button>

      {/* Quick actions grid */}
      <div className="grid grid-cols-2 gap-3">
        <QuickAction icon={QrCode} label="Escanear QR Code" desc="Ativar câmera de segurança" color="cyan" onClick={() => setScreen("qr_scan")} />
        <QuickAction icon={Camera} label="Reportar Agressor" desc="Enviar foto para análise" color="amber" onClick={() => setScreen("report_aggressor")} />
        <QuickAction icon={UserPlus} label="Cadastrar Agressor" desc="Pré-cadastrar para IA" color="red" onClick={() => setScreen("register_aggressor")} />
        <QuickAction icon={Car} label="Serviços" desc="Uber, 99, iFood" color="purple" onClick={() => setScreen("services")} />
        <QuickAction icon={MapPinned} label="Trajeto Protegido" desc="Central acompanha até o destino" color="cyan" onClick={() => setScreen("trip")} />
      </div>

      {/* Pending events */}
      {pendingEvents.length > 0 && (
        <div>
          <h3 className="text-xs font-semibold uppercase tracking-wider text-white/40 mb-2">Seus Chamados</h3>
          <div className="space-y-2">
            {pendingEvents.slice(0, 3).map((evt, i) => (
              <div key={i} className="flex items-center gap-3 p-3 rounded-lg bg-white/5 border border-white/10">
                <div className={`w-2 h-2 rounded-full ${evt.status === "resolved" ? "bg-green-400" : "bg-amber-400 animate-pulse"}`} />
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium truncate">{evt.description || "Chamado de emergência"}</p>
                  <p className="text-[10px] text-white/40">{new Date(evt.createdAt).toLocaleTimeString("pt-BR")}</p>
                </div>
                <Badge className={`text-[9px] ${evt.status === "resolved" ? "bg-green-500/20 text-green-400" : "bg-amber-500/20 text-amber-400"}`}>
                  {evt.status === "resolved" ? "Resolvido" : "Em andamento"}
                </Badge>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Info section */}
      <div className="p-4 rounded-xl bg-white/5 border border-white/10">
        <h3 className="text-sm font-semibold text-white/80 mb-2">Informações do Estádio</h3>
        <div className="space-y-2">
          <InfoRow icon={MapPin} label="Local" value="MorumBIS — São Paulo, SP" />
          <InfoRow icon={Shield} label="Segurança" value="ELLA by GAP — Ativa" />
          <InfoRow icon={Phone} label="Emergência" value="Toque no botão SOS" />
        </div>
        <button onClick={() => setScreen("info")} className="mt-3 text-xs text-purple-400 hover:underline cursor-pointer">
          Ver mais informações →
        </button>
      </div>

      {/* Ad space */}
      <div className="p-4 rounded-xl bg-gradient-to-r from-purple-900/20 via-indigo-900/20 to-purple-900/20 border border-purple-500/10 text-center">
        <p className="text-[10px] text-purple-300/40 uppercase tracking-widest mb-1">Patrocinador</p>
        <p className="text-sm font-semibold text-white/30">Espaço Publicitário Premium</p>
        <p className="text-[10px] text-white/20 mt-1">Anuncie aqui — contato@gapseguranca.com.br</p>
      </div>
    </div>
  );
}

function QuickAction({ icon: Icon, label, desc, color, onClick }: {
  icon: any; label: string; desc: string; color: string; onClick: () => void;
}) {
  const colorMap: Record<string, string> = {
    cyan: "from-cyan-500/10 to-cyan-500/5 border-cyan-500/20",
    amber: "from-amber-500/10 to-amber-500/5 border-amber-500/20",
    purple: "from-purple-500/10 to-purple-500/5 border-purple-500/20",
    red: "from-red-500/10 to-red-500/5 border-red-500/20",
  };
  const iconColor: Record<string, string> = {
    cyan: "text-cyan-400", amber: "text-amber-400", purple: "text-purple-400", red: "text-red-400",
  };
  return (
    <button
      onClick={onClick}
      className={`p-4 rounded-xl bg-gradient-to-br ${colorMap[color]} border flex flex-col items-start gap-2 cursor-pointer hover:scale-[1.02] transition-transform active:scale-[0.98]`}
    >
      <Icon className={`w-6 h-6 ${iconColor[color]}`} />
      <div>
        <p className="text-xs font-semibold text-white/80">{label}</p>
        <p className="text-[10px] text-white/40">{desc}</p>
      </div>
    </button>
  );
}

function InfoRow({ icon: Icon, label, value }: { icon: any; label: string; value: string }) {
  return (
    <div className="flex items-center gap-2">
      <Icon className="w-3.5 h-3.5 text-purple-400/60 shrink-0" />
      <span className="text-[10px] text-white/40 w-20 shrink-0">{label}</span>
      <span className="text-[10px] text-white/70">{value}</span>
    </div>
  );
}

/* ─── EMERGENCY SCREEN ─── */
function EmergencyScreen({ socket, socketConnected, presenceId, userSector, userName, setScreen, setPendingEvents, qrEnvironment, qrCode, qrSector, operatorCameraRequest }: {
  socket: any; socketConnected: boolean; presenceId: string; userSector: string; userName: string;
  setScreen: (s: AppScreen) => void; setPendingEvents: React.Dispatch<React.SetStateAction<any[]>>;
  qrEnvironment?: string; qrCode?: string; qrSector?: string; operatorCameraRequest?: number;
}) {
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const sentRef = useRef(false);
  const [description, setDescription] = useState("");
  const [location, setLocation] = useState<{ lat: number; lng: number } | null>(null);
  const [cameraActive, setCameraActive] = useState(false);
  const emergencyVideoRef = useRef<HTMLVideoElement>(null);
  const emergencyStreamRef = useRef<MediaStream | null>(null);
  const emergencyCanvasRef = useRef<HTMLCanvasElement>(null);

  const [gpsStatus, setGpsStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const gpsWatchRef = useRef<number | null>(null);

  // V10.2 Audit: Use watchPosition (reliable on mobile) with fallback
  const startGPS = useCallback(() => {
    if (!navigator.geolocation) {
      setGpsStatus('error');
      toast.error("GPS não disponível neste dispositivo.");
      return;
    }
    setGpsStatus('loading');

    // Clear any existing watch
    if (gpsWatchRef.current !== null) {
      navigator.geolocation.clearWatch(gpsWatchRef.current);
      gpsWatchRef.current = null;
    }

    let gotPosition = false;

    // Strategy: try watchPosition with high accuracy first
    gpsWatchRef.current = navigator.geolocation.watchPosition(
      (pos) => {
        gotPosition = true;
        setLocation({ lat: pos.coords.latitude, lng: pos.coords.longitude });
        setGpsStatus('success');
      },
      (err) => {
        console.warn("[GPS] watchPosition error:", err.code, err.message);
        // If high accuracy fails, try without
        if (!gotPosition) {
          if (gpsWatchRef.current !== null) {
            navigator.geolocation.clearWatch(gpsWatchRef.current);
          }
          // Fallback: lower accuracy
          gpsWatchRef.current = navigator.geolocation.watchPosition(
            (pos) => {
              gotPosition = true;
              setLocation({ lat: pos.coords.latitude, lng: pos.coords.longitude });
              setGpsStatus('success');
            },
            (err2) => {
              console.warn("[GPS] fallback watchPosition error:", err2.code, err2.message);
              setGpsStatus('error');
            },
            { enableHighAccuracy: false, timeout: 20000, maximumAge: 120000 }
          );
        }
      },
      { enableHighAccuracy: true, timeout: 12000, maximumAge: 10000 }
    );

    // Safety timeout: if no position after 25s, show error
    setTimeout(() => {
      if (!gotPosition) {
        setGpsStatus(prev => prev === 'loading' ? 'error' : prev);
      }
    }, 25000);
  }, []);

  useEffect(() => {
    startGPS();
    return () => {
      if (gpsWatchRef.current !== null) {
        navigator.geolocation.clearWatch(gpsWatchRef.current);
      }
    };
  }, [startGPS]);

  const sendEmergency = useCallback(() => {
    if (!socket || !socketConnected) {
      toast.error("Sem conexão. Tentando novamente...");
      return;
    }
    setSending(true);
    // Create a session and emit emergency event
    const emergencyData = {
      type: "emergency",
      clientId: presenceId,
      sector: userSector || qrSector || "Não informado",
      reportedBy: userName || "Usuário do App",
      description: description || "Acionamento de emergência via App ELLA",
      location,
      timestamp: Date.now(),
      source: "mobile_app",
      qrEnvironment: qrEnvironment || undefined,
      qrCode: qrCode || undefined,
    };
    socket.emit("mobile:emergency", emergencyData);
    // Listen for acknowledgment
    const handleAck = (data: any) => {
      sentRef.current = true;
      setSending(false);
      setSent(true);
      setPendingEvents(prev => [...prev, { ...emergencyData, eventId: data.eventId, status: "active", createdAt: Date.now() }]);
      toast.success("Emergência registrada! Agente será acionado.", { duration: 5000 });
      socket.off("mobile:emergency-ack", handleAck);
      // V8.8: Start continuous GPS tracking after emergency confirmed
      if (data.eventId) {
        const watchId = navigator.geolocation.watchPosition(
          (pos) => {
                          socket.emit("mobile:location-update", {
                clientId: presenceId,
                eventId: data.eventId,

              lat: pos.coords.latitude,
              lng: pos.coords.longitude,
              accuracy: pos.coords.accuracy,
              reportedBy: userName || "Usuário App",
            });
          },
          () => {},
          { enableHighAccuracy: true, maximumAge: 3000, timeout: 10000 }
        );
        // Stop tracking after 30 minutes
        setTimeout(() => navigator.geolocation.clearWatch(watchId), 30 * 60 * 1000);
      }
    };
    socket.on("mobile:emergency-ack", handleAck);
    // Timeout fallback - uses ref to avoid stale closure
    setTimeout(() => {
      if (!sentRef.current) {
        sentRef.current = true;
        setSending(false);
        setSent(true);
        setPendingEvents(prev => [...prev, { ...emergencyData, status: "pending_sync", createdAt: Date.now() }]);
        toast.info("Emergência registrada localmente. Será sincronizada quando possível.");
        socket.off("mobile:emergency-ack", handleAck);
      }
    }, 10000);
  }, [socket, socketConnected, presenceId, userSector, userName, description, location, sent, setPendingEvents]);

  // V9.1: Hardened camera initialization (Connect.tsx pattern)
  const startEmergencyCamera = useCallback(async () => {
    try {
      let stream: MediaStream;
      try {
        stream = await safeGetUserMedia({
          video: { facingMode: { ideal: 'environment' }, width: { ideal: 640 }, height: { ideal: 480 } },
          audio: true,
        });
      } catch (frontErr) {
        console.warn('[EmergencyCamera] Preferred camera failed, trying fallback:', frontErr);
        stream = await safeGetUserMedia({ video: true, audio: true });
      }
      emergencyStreamRef.current = stream;

      const video = emergencyVideoRef.current;
      if (video) {
        // Reset video element completely
        video.srcObject = null;
        video.removeAttribute('src');
        video.load();
        // Mobile-critical attributes
        video.setAttribute('autoplay', '');
        video.setAttribute('playsinline', '');
        video.setAttribute('muted', '');
        video.setAttribute('webkit-playsinline', 'true');
        video.muted = true;
        video.playsInline = true;
        // Assign stream
        video.srcObject = stream;
        // Wait for video to be ready
        await new Promise<void>((resolve) => {
          const timeout = setTimeout(() => { resolve(); }, 6000);
          const onPlaying = () => {
            clearTimeout(timeout);
            video.removeEventListener('playing', onPlaying);
            resolve();
          };
          const onMetadata = () => {
            video.removeEventListener('loadedmetadata', onMetadata);
            video.play().then(() => { clearTimeout(timeout); resolve(); }).catch(() => {
              setTimeout(() => { video.play().catch(() => {}); clearTimeout(timeout); resolve(); }, 300);
            });
          };
          video.addEventListener('playing', onPlaying);
          video.addEventListener('loadedmetadata', onMetadata);
          video.play().catch(() => {});
        });
      }
      setCameraActive(true);
      if (operatorCameraRequest && socket) socket.emit("mobile:camera-request-ack", { clientId: presenceId, status: "streaming" });
      toast.success('Câmera ativada');

      // Send frames to server every 3 seconds
      const canvas = emergencyCanvasRef.current;
      if (canvas) {
        const ctx = canvas.getContext('2d');
        let victimDescriptorSent = false;
        const frameInterval = setInterval(async () => {
          if (emergencyVideoRef.current && ctx && socket) {
            canvas.width = 320;
            canvas.height = 240;
            ctx.drawImage(emergencyVideoRef.current, 0, 0, 320, 240);
            const frame = canvas.toDataURL('image/jpeg', 0.5);
            socket.emit('mobile:emergency-frame', { clientId: presenceId, frame, reportedBy: userName || 'Usuário App' });

            // V10: On first good frame, try to detect victim face and send descriptor
            if (!victimDescriptorSent && faceapi) {
              try {
                const detection = await faceapi.detectSingleFace(emergencyVideoRef.current).withFaceLandmarks(true).withFaceDescriptor();
                if (detection && detection.detection.score >= 0.6) {
                  const descriptor = Array.from(detection.descriptor);
                  socket.emit('mobile:victim-descriptor', { clientId: presenceId, descriptor, reportedBy: userName || 'Usuário App' });
                  victimDescriptorSent = true;
                  console.log('[EmergencyCamera] Victim descriptor captured and sent');
                }
              } catch (e) { /* face-api not loaded yet, skip */ }
            }
          }
        }, 3000);
        setTimeout(() => { clearInterval(frameInterval); }, 10 * 60 * 1000);
      }
    } catch (err) {
      console.error('[EmergencyCamera] Failed:', err);
      if (operatorCameraRequest && socket) socket.emit("mobile:camera-request-ack", { clientId: presenceId, status: "error", message: "Não foi possível ativar câmera/microfone" });
      toast.error('Não foi possível ativar a câmera. Verifique as permissões.');
    }
  }, [socket, presenceId, userName, operatorCameraRequest]);


  const stopEmergencyCamera = useCallback(() => {
    if (emergencyStreamRef.current) {
      emergencyStreamRef.current.getTracks().forEach(t => t.stop());
      emergencyStreamRef.current = null;
    }
    setCameraActive(false);
  }, []);

  if (operatorCameraRequest && !cameraActive && !sent) {
    return (
      <div className="px-4 py-6 space-y-4">
        <div className="p-5 rounded-2xl border border-red-500/40 bg-red-500/10 text-center">
          <Camera className="w-10 h-10 text-red-300 mx-auto mb-3" />
          <h2 className="text-lg font-bold text-white">Solicitação da Central</h2>
          <p className="text-sm text-white/70 mt-2">A Central ELLA solicita acesso à sua câmera e ao microfone para acompanhamento de segurança.</p>
          {!getBrowserCapabilities().secureContext && <p className="text-xs text-amber-300 mt-3">Este acesso está em HTTP. O app continua carregado, mas câmera, microfone e GPS ficam indisponíveis até HTTPS.</p>}
          <div className="grid grid-cols-2 gap-2 mt-5">
            <Button onClick={startEmergencyCamera} className="bg-red-600 hover:bg-red-700 text-white"><Camera className="w-4 h-4 mr-2" /> Permitir</Button>
            <Button variant="outline" onClick={() => { socket?.emit("mobile:camera-request-ack", { clientId: presenceId, status: "error", message: "Usuária recusou a solicitação" }); setScreen("home"); }} className="border-white/20 text-white/70">Agora não</Button>
          </div>
        </div>
      </div>
    );
  }

  if (sent) {
    return (
      <div className="px-4 py-6 space-y-4">
        <div className="text-center">
          <div className="w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center mx-auto">
            <CheckCircle2 className="w-8 h-8 text-green-400" />
          </div>
          <h2 className="text-lg font-bold text-green-300 mt-2">Emergência Registrada</h2>
          <p className="text-xs text-white/50 mt-1">Agente acionado. Sua localização está sendo compartilhada.</p>
        </div>

        {/* Optional: Camera and Audio buttons */}
        {!cameraActive && (
          <div className="space-y-2">
            <p className="text-xs text-white/40 text-center">Deseja compartilhar câmera ou áudio com a central?</p>
            <div className="flex gap-2">
              <Button onClick={startEmergencyCamera} className="flex-1 bg-red-600 hover:bg-red-700 cursor-pointer text-sm">
                <Camera className="w-4 h-4 mr-2" /> Abrir Câmera
              </Button>
            <Button onClick={async () => {
                const existingRecorder = (window as any).__ellaAudioRecorder;
                if (existingRecorder && existingRecorder.state === 'recording') {
                  // STOP recording
                  existingRecorder.stop();
                  (window as any).__ellaAudioRecorder = null;
                  toast.success('Áudio enviado para a central!');
                  return;
                }
                try {
                  const audioStream = await safeGetUserMedia({ audio: true });
                  const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus') ? 'audio/webm;codecs=opus' : 'audio/webm';
                  const recorder = new MediaRecorder(audioStream, { mimeType });
                  const chunks: Blob[] = [];
                  recorder.ondataavailable = (e) => {
                    if (e.data.size > 0) {
                      chunks.push(e.data);
                      const reader = new FileReader();
                      reader.onloadend = () => {
                        if (socket && reader.result) socket.emit('mobile:emergency-audio-stream', { clientId: presenceId, audioChunk: reader.result, reportedBy: userName || 'Usuário App', mimeType, timestamp: Date.now() });
                      };
                      reader.readAsDataURL(e.data);
                    }
                  };
                  recorder.onstop = () => {
                    audioStream.getTracks().forEach(t => t.stop());
                    const blob = new Blob(chunks, { type: mimeType });
                    const reader = new FileReader();
                    reader.onloadend = () => {
                      if (socket && reader.result) {
                        socket.emit('mobile:emergency-audio', { clientId: presenceId, audio: reader.result, reportedBy: userName || 'Usuário App', mimeType });
                      }
                    };
                    reader.readAsDataURL(blob);
                  };
                  recorder.start(1000); // timeslice 1s for chunked data
                  (window as any).__ellaAudioRecorder = recorder;
                  toast.success('Gravando áudio... Toque novamente para PARAR e enviar.', { duration: 60000 });
                  // Auto-stop after 60 seconds
                  setTimeout(() => { if (recorder.state === 'recording') { recorder.stop(); (window as any).__ellaAudioRecorder = null; } }, 60000);
                } catch {
                  toast.error('Não foi possível ativar o microfone. Verifique as permissões.');
                }
              }} className={`flex-1 cursor-pointer text-sm ${(window as any).__ellaAudioRecorder?.state === 'recording' ? 'bg-red-600 hover:bg-red-700 animate-pulse' : 'bg-amber-600 hover:bg-amber-700'}`}>
                <Megaphone className="w-4 h-4 mr-2" /> {(window as any).__ellaAudioRecorder?.state === 'recording' ? 'Parar Áudio' : 'Abrir Áudio'}
              </Button></div>
          </div>
        )}

        {/* Camera feed when active */}
        {cameraActive && (
          <div className="space-y-2">
            <div className="relative rounded-lg overflow-hidden bg-black border border-white/10">
              <video ref={emergencyVideoRef} autoPlay playsInline muted className="w-full h-48 object-cover" />
              <canvas ref={emergencyCanvasRef} className="hidden" />
              <div className="absolute top-2 right-2 flex items-center gap-1 bg-red-600/80 px-2 py-0.5 rounded text-[10px] text-white">
                <div className="w-2 h-2 rounded-full bg-white animate-pulse" /> AO VIVO
              </div>
            </div>
            <Button onClick={stopEmergencyCamera} variant="outline" className="w-full text-xs border-red-500/30 text-red-400 cursor-pointer">
              Desativar Câmera
            </Button>
          </div>
        )}

        {location && (
          <div className="p-2 rounded bg-white/5 border border-white/10 flex items-center gap-2">
            <MapPin className="w-3.5 h-3.5 text-green-400" />
            <span className="text-[10px] text-green-400 font-mono">{location.lat.toFixed(6)}, {location.lng.toFixed(6)}</span>
            <span className="text-[10px] text-white/30 ml-auto">GPS ativo</span>
          </div>
        )}

        <Button onClick={() => { stopEmergencyCamera(); setSent(false); setScreen("home"); }} className="w-full bg-purple-600 hover:bg-purple-700 cursor-pointer">
          Voltar ao Início
        </Button>
      </div>
    );
  }

  return (
    <div className="px-4 py-6 space-y-6">
      <div className="text-center">
        <div className="w-20 h-20 rounded-full bg-red-500/20 flex items-center justify-center mx-auto mb-4 animate-pulse">
          <AlertTriangle className="w-10 h-10 text-red-400" />
        </div>
        <h2 className="text-xl font-bold text-red-300">ACIONAMENTO DE EMERGÊNCIA</h2>
        <p className="text-sm text-white/50 mt-1">Um agente será enviado ao seu local</p>
      </div>

      {gpsStatus === 'loading' && !location && (
        <div className="p-3 rounded-lg bg-white/5 border border-amber-500/20 flex items-center gap-2">
          <Loader2 className="w-4 h-4 text-amber-400 animate-spin" />
          <span className="text-xs text-amber-300">Obtendo localização GPS...</span>
        </div>
      )}
      {gpsStatus === 'error' && !location && (
        <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/20 space-y-2">
          <div className="flex items-center gap-2">
            <MapPin className="w-4 h-4 text-red-400" />
            <span className="text-xs text-red-300">GPS indisponível. Verifique permissões de localização.</span>
          </div>
          <Button
            onClick={startGPS}
            size="sm"
            className="w-full text-xs bg-amber-600 hover:bg-amber-700 cursor-pointer"
          >
            <MapPinned className="w-3 h-3 mr-1" /> Tentar Novamente
          </Button>
        </div>
      )}
      {location && (
        <div className="p-3 rounded-lg bg-green-500/10 border border-green-500/20 flex items-center gap-2">
          <MapPin className="w-4 h-4 text-green-400" />
          <span className="text-xs text-green-300 font-mono">{location.lat.toFixed(6)}, {location.lng.toFixed(6)}</span>
          <span className="text-[10px] text-green-400/60 ml-auto">GPS ativo</span>
        </div>
      )}

      <div>
        <label className="text-xs text-white/40 mb-1 block">Descreva a situação (opcional)</label>
        <textarea
          value={description}
          onChange={e => setDescription(e.target.value)}
          placeholder="Ex: Briga no setor Norte, homem agredindo mulher..."
          className="w-full h-24 rounded-lg bg-white/5 border border-white/10 p-3 text-sm text-white placeholder:text-white/20 resize-none focus:outline-none focus:border-purple-500/40"
        />
      </div>

      <Button
        onClick={sendEmergency}
        disabled={sending}
        className="w-full py-6 text-lg bg-red-600 hover:bg-red-700 text-white cursor-pointer disabled:opacity-50"
      >
        {sending ? <Loader2 className="w-5 h-5 animate-spin mr-2" /> : <AlertTriangle className="w-5 h-5 mr-2" />}
        {sending ? "ENVIANDO..." : "ACIONAR EMERGÊNCIA"}
      </Button>

      <p className="text-[10px] text-center text-white/30">
        Ao acionar, sua localização será compartilhada com a equipe de segurança.
        Trotes serão registrados e podem resultar em penalidades.
      </p>
    </div>
  );
}

/* ─── QR SCAN SCREEN ─── */
function QRScanScreen({ socket, socketConnected, setScreen }: {
  socket: any; socketConnected: boolean; setScreen: (s: AppScreen) => void;
}) {
  const scannerRef = useRef<HTMLDivElement>(null);
  const [scanning, setScanning] = useState(false);
  const [result, setResult] = useState<string | null>(null);
  const html5QrCodeRef = useRef<any>(null);

  const startScanner = useCallback(async () => {
    if (!scannerRef.current || scanning) return;
    setScanning(true);
    try {
      const { Html5Qrcode } = await import("html5-qrcode");
      const scanner = new Html5Qrcode("qr-reader");
      html5QrCodeRef.current = scanner;
      await scanner.start(
        { facingMode: "environment" },
        { fps: 10, qrbox: { width: 250, height: 250 } },
        (decodedText: string) => {
          setResult(decodedText);
          scanner.stop().catch(() => {});
          setScanning(false);
          // If it's a connect URL, navigate to it
          if (decodedText.includes("/connect/")) {
            const code = decodedText.split("/connect/").pop();
            if (code) {
              window.location.href = `/connect/${code}`;
            }
          } else {
            // Emit QR scan event
            if (socket && socketConnected) {
              socket.emit("mobile:qr-scanned", { qrData: decodedText, timestamp: Date.now() });
            }
            toast.success("QR Code lido com sucesso!");
          }
        },
        () => {} // ignore errors during scanning
      );
    } catch (err: any) {
      setScanning(false);
      toast.error("Erro ao acessar câmera: " + (err.message || "Permissão negada"));
    }
  }, [scanning, socket, socketConnected]);

  useEffect(() => {
    return () => {
      if (html5QrCodeRef.current) {
        html5QrCodeRef.current.stop().catch(() => {});
      }
    };
  }, []);

  return (
    <div className="px-4 py-6 space-y-6">
      <div className="text-center">
        <QrCode className="w-12 h-12 text-cyan-400 mx-auto mb-3" />
        <h2 className="text-lg font-bold text-white">Escanear QR Code</h2>
        <p className="text-xs text-white/50 mt-1">Aponte para o QR Code no estádio para ativar a câmera de segurança</p>
      </div>

      <div className="relative rounded-xl overflow-hidden border border-cyan-500/20 bg-black" style={{ minHeight: "300px" }}>
        <div id="qr-reader" ref={scannerRef} className="w-full" />
        {!scanning && !result && (
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <Camera className="w-12 h-12 text-white/20 mb-3" />
            <Button onClick={startScanner} className="bg-cyan-600 hover:bg-cyan-700 cursor-pointer">
              <Camera className="w-4 h-4 mr-2" /> Abrir Câmera
            </Button>
          </div>
        )}
      </div>

      {result && (
        <div className="p-4 rounded-xl bg-green-500/10 border border-green-500/20">
          <div className="flex items-center gap-2 mb-2">
            <CheckCircle2 className="w-5 h-5 text-green-400" />
            <span className="text-sm font-semibold text-green-300">QR Code Lido!</span>
          </div>
          <p className="text-xs text-white/50 break-all">{result}</p>
          <div className="flex gap-2 mt-3">
            <Button onClick={() => { setResult(null); startScanner(); }} variant="outline" className="flex-1 cursor-pointer text-xs">
              Escanear Outro
            </Button>
            <Button onClick={() => setScreen("home")} className="flex-1 bg-purple-600 hover:bg-purple-700 cursor-pointer text-xs">
              Voltar
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

/* ─── CHECK-IN SCREEN ─── */
function CheckinScreen({ socket, socketConnected, presenceId, setUserName, setUserCheckedIn, setUserSector, setScreen }: {
  socket: any; socketConnected: boolean; presenceId: string;
  setUserName: (n: string) => void; setUserCheckedIn: (b: boolean) => void;
  setUserSector: (s: string) => void; setScreen: (s: AppScreen) => void;
}) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [cameraActive, setCameraActive] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [checkedIn, setCheckedIn] = useState(false);
  const [personName, setPersonName] = useState("");
  const [sector, setSector] = useState("");
  const [modelsLoaded, setModelsLoaded] = useState(false);

  const startCamera = useCallback(async () => {
    try {
      let stream: MediaStream;
      try {
        stream = await safeGetUserMedia({
          video: { facingMode: 'user', width: { ideal: 640 }, height: { ideal: 480 } },
        });
      } catch {
        stream = await safeGetUserMedia({ video: true });
      }
      const video = videoRef.current;
      if (video) {
        video.srcObject = null;
        video.removeAttribute('src');
        video.load();
        video.setAttribute('autoplay', '');
        video.setAttribute('playsinline', '');
        video.setAttribute('muted', '');
        video.setAttribute('webkit-playsinline', 'true');
        video.muted = true;
        video.playsInline = true;
        video.srcObject = stream;
        await new Promise<void>((resolve) => {
          const timeout = setTimeout(() => resolve(), 6000);
          const onPlaying = () => { clearTimeout(timeout); video.removeEventListener('playing', onPlaying); resolve(); };
          const onMeta = () => {
            video.removeEventListener('loadedmetadata', onMeta);
            video.play().then(() => { clearTimeout(timeout); resolve(); }).catch(() => {
              setTimeout(() => { video.play().catch(() => {}); clearTimeout(timeout); resolve(); }, 300);
            });
          };
          video.addEventListener('playing', onPlaying);
          video.addEventListener('loadedmetadata', onMeta);
          video.play().catch(() => {});
        });
        setCameraActive(true);
      }
      // Load face-api models
      if (!faceapi) {
        faceapi = await import("@vladmandic/face-api");
      }
      if (!modelsLoaded) {
        const MODEL_URL = "/api/face-models";
        await Promise.all([
          faceapi.nets.ssdMobilenetv1.loadFromUri(MODEL_URL),
          faceapi.nets.faceLandmark68TinyNet.loadFromUri(MODEL_URL),
          faceapi.nets.faceRecognitionNet.loadFromUri(MODEL_URL),
        ]);
        setModelsLoaded(true);
      }
    } catch (err) {
      console.error('[CheckinCamera] Failed:', err);
      toast.error("Erro ao acessar câmera. Verifique as permissões.");
    }
  }, [modelsLoaded]);

  const captureAndIdentify = useCallback(async () => {
    if (!videoRef.current || !faceapi || !socket || !socketConnected) return;
    setProcessing(true);
    try {
      const detection = await faceapi.detectSingleFace(videoRef.current).withFaceLandmarks(true).withFaceDescriptor();
      if (!detection) {
        toast.error("Nenhum rosto detectado. Posicione-se melhor.");
        setProcessing(false);
        return;
      }
      // V10: Only accept faces with >60% detection confidence
      if (detection.detection.score < 0.6) {
        toast.error(`Confiança baixa (${Math.round(detection.detection.score * 100)}%). Aproxime-se e tente novamente.`);
        setProcessing(false);
        return;
      }
      const descriptor = Array.from(detection.descriptor);
      // Capture photo
      const canvas = canvasRef.current;
      if (canvas && videoRef.current) {
        canvas.width = videoRef.current.videoWidth;
        canvas.height = videoRef.current.videoHeight;
        canvas.getContext("2d")?.drawImage(videoRef.current, 0, 0);
      }
      // Send to backend for identification via dedicated mobile:checkin handler
      socket.emit("mobile:checkin", {
        clientId: presenceId,
        descriptor,
        imageDataUrl: canvas?.toDataURL("image/jpeg", 0.7) || "",
        sector: sector || "Geral",
        source: "mobile_checkin",
      });
      // Listen for result
      const handleResult = (data: any) => {
        setProcessing(false);
        if (data.matched) {
          setPersonName(data.personName || "Identificado");
          setCheckedIn(true);
          setUserName(data.personName || "Identificado");
          setUserCheckedIn(true);
          setUserSector(sector || "Geral");
          toast.success(`Check-in realizado: ${data.personName}`);
        } else {
          toast.info("Rosto não cadastrado. Check-in como visitante.");
          setCheckedIn(true);
          setUserName("Visitante");
          setUserCheckedIn(true);
          setUserSector(sector || "Geral");
        }
        socket.off("face:result", handleResult);
      };
      socket.on("face:result", handleResult);
      setTimeout(() => {
        setProcessing(false);
        socket.off("face:result", handleResult);
      }, 10000);
    } catch (err) {
      setProcessing(false);
      toast.error("Erro no reconhecimento facial");
    }
  }, [socket, socketConnected, presenceId, sector, setUserName, setUserCheckedIn, setUserSector]);

  useEffect(() => {
    return () => {
      if (videoRef.current?.srcObject) {
        (videoRef.current.srcObject as MediaStream).getTracks().forEach(t => t.stop());
      }
    };
  }, []);

  if (checkedIn) {
    return (
      <div className="px-4 py-12 text-center space-y-6">
        <div className="w-20 h-20 rounded-full bg-green-500/20 flex items-center justify-center mx-auto">
          <CheckCircle2 className="w-10 h-10 text-green-400" />
        </div>
        <div>
          <h2 className="text-xl font-bold text-green-300">Check-in Realizado!</h2>
          <p className="text-sm text-white/50 mt-2">{personName || "Visitante"} — {sector || "Geral"}</p>
        </div>
        <Button onClick={() => setScreen("home")} className="bg-purple-600 hover:bg-purple-700 cursor-pointer">
          Voltar ao Início
        </Button>
      </div>
    );
  }

  return (
    <div className="px-4 py-6 space-y-6">
      <div className="text-center">
        <ScanFace className="w-12 h-12 text-purple-400 mx-auto mb-3" />
        <h2 className="text-lg font-bold text-white">Check-in Facial</h2>
        <p className="text-xs text-white/50 mt-1">Seu rosto será reconhecido automaticamente</p>
      </div>

      <div>
        <label className="text-xs text-white/40 mb-1 block">Seu setor (opcional)</label>
        <input
          value={sector}
          onChange={e => setSector(e.target.value)}
          placeholder="Ex: Norte A, Sul B, Camarote..."
          className="w-full rounded-lg bg-white/5 border border-white/10 p-3 text-sm text-white placeholder:text-white/20 focus:outline-none focus:border-purple-500/40"
        />
      </div>

      <div className="relative rounded-xl overflow-hidden border border-purple-500/20 bg-black" style={{ minHeight: "300px" }}>
        <video ref={videoRef} className="w-full h-auto" playsInline muted />
        <canvas ref={canvasRef} className="hidden" />
        {!cameraActive && (
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <Camera className="w-12 h-12 text-white/20 mb-3" />
            <Button onClick={startCamera} className="bg-purple-600 hover:bg-purple-700 cursor-pointer">
              <Camera className="w-4 h-4 mr-2" /> Abrir Câmera
            </Button>
          </div>
        )}
        {processing && (
          <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
            <div className="text-center">
              <Loader2 className="w-8 h-8 text-purple-400 animate-spin mx-auto mb-2" />
              <p className="text-xs text-white/70">Identificando...</p>
            </div>
          </div>
        )}
      </div>

      {cameraActive && !processing && (
        <Button onClick={captureAndIdentify} className="w-full py-4 bg-purple-600 hover:bg-purple-700 cursor-pointer">
          <ScanFace className="w-5 h-5 mr-2" /> Capturar e Identificar
        </Button>
      )}
    </div>
  );
}

/* ─── REPORT AGGRESSOR SCREEN ─── */
function ReportAggressorScreen({ socket, socketConnected, userName, userSector, setScreen }: {
  socket: any; socketConnected: boolean; userName: string; userSector: string;
  setScreen: (s: AppScreen) => void;
}) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [cameraActive, setCameraActive] = useState(false);
  const [capturedPhoto, setCapturedPhoto] = useState<string | null>(null);
  const [description, setDescription] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [detecting, setDetecting] = useState(false);
  const [faceDescriptor, setFaceDescriptor] = useState<number[] | null>(null);
  const [matchResult, setMatchResult] = useState<{ matched: boolean; personName?: string; confidence?: number } | null>(null);

  const startCamera = useCallback(async () => {
    try {
      let stream: MediaStream;
      try {
        stream = await safeGetUserMedia({
          video: { facingMode: { ideal: 'environment' }, width: { ideal: 640 }, height: { ideal: 480 } },
        });
      } catch {
        stream = await safeGetUserMedia({ video: true });
      }
      const video = videoRef.current;
      if (video) {
        video.srcObject = null;
        video.removeAttribute('src');
        video.load();
        video.setAttribute('autoplay', '');
        video.setAttribute('playsinline', '');
        video.setAttribute('muted', '');
        video.setAttribute('webkit-playsinline', 'true');
        video.muted = true;
        video.playsInline = true;
        video.srcObject = stream;
        await new Promise<void>((resolve) => {
          const timeout = setTimeout(() => resolve(), 6000);
          const onPlaying = () => { clearTimeout(timeout); video.removeEventListener('playing', onPlaying); resolve(); };
          const onMeta = () => {
            video.removeEventListener('loadedmetadata', onMeta);
            video.play().then(() => { clearTimeout(timeout); resolve(); }).catch(() => {
              setTimeout(() => { video.play().catch(() => {}); clearTimeout(timeout); resolve(); }, 300);
            });
          };
          video.addEventListener('playing', onPlaying);
          video.addEventListener('loadedmetadata', onMeta);
          video.play().catch(() => {});
        });
        setCameraActive(true);
      }
    } catch {
      toast.error("Erro ao acessar câmera. Verifique as permissões.");
    }
  }, []);

  const capturePhoto = useCallback(async () => {
    if (!videoRef.current || !canvasRef.current) return;
    setDetecting(true);
    const canvas = canvasRef.current;
    canvas.width = videoRef.current.videoWidth;
    canvas.height = videoRef.current.videoHeight;
    canvas.getContext("2d")?.drawImage(videoRef.current, 0, 0);
    const dataUrl = canvas.toDataURL("image/jpeg", 0.8);
    setCapturedPhoto(dataUrl);

    // Extract face descriptor for database matching
    try {
      if (!faceapi) {
        try {
          faceapi = await import("@vladmandic/face-api");
          await faceapi.nets.tinyFaceDetector.loadFromUri("/api/face-models");
          await faceapi.nets.faceLandmark68TinyNet.loadFromUri("/api/face-models");
          await faceapi.nets.faceRecognitionNet.loadFromUri("/api/face-models");
        } catch {
          // Face detection not available on this device
          setDetecting(false);
          (videoRef.current.srcObject as MediaStream)?.getTracks().forEach(t => t.stop());
          setCameraActive(false);
          return;
        }
      }
      const detection = await faceapi
        .detectSingleFace(videoRef.current, new faceapi.TinyFaceDetectorOptions({ inputSize: 320, scoreThreshold: 0.45 }))
        .withFaceLandmarks(true)
        .withFaceDescriptor();
      if (detection) {
        const descriptor = Array.from(detection.descriptor) as number[];
        setFaceDescriptor(descriptor);
        toast.success("Rosto detectado! Será comparado com o banco de dados.");
      } else {
        toast.info("Foto capturada, mas nenhum rosto detectado claramente.");
      }
    } catch {
      // Silently continue without descriptor
    }
    setDetecting(false);
    // Stop camera
    (videoRef.current.srcObject as MediaStream)?.getTracks().forEach(t => t.stop());
    setCameraActive(false);
  }, []);

  const sendReport = useCallback(() => {
    if (!socket || !socketConnected) {
      toast.error("Sem conexão. Relatório salvo localmente.");
      setSent(true);
      return;
    }
    setSending(true);
    socket.emit("mobile:report-aggressor", {
      photo: capturedPhoto,
      description,
      reportedBy: userName || "Anônimo",
      sector: userSector || "Não informado",
      timestamp: Date.now(),
      source: "mobile_app",
      faceDescriptor: faceDescriptor || undefined,
    });
    // Listen for server ACK (now includes match result)
    const handleAck = (data: any) => {
      setSending(false);
      setSent(true);
      if (data?.matchResult) {
        setMatchResult(data.matchResult);
        if (data.matchResult.matched) {
          toast.success(`Agressor IDENTIFICADO: ${data.matchResult.personName}`, { duration: 8000 });
        } else {
          toast.info("Pessoa não encontrada no banco de dados.", { duration: 5000 });
        }
      } else {
        toast.success("Relatório enviado para análise!");
      }
      socket.off("mobile:report-ack", handleAck);
    };
    socket.on("mobile:report-ack", handleAck);
    // Timeout fallback
    setTimeout(() => {
      if (!sent) {
        setSending(false);
        setSent(true);
        toast.success("Relatório enviado para análise!");
        socket.off("mobile:report-ack", handleAck);
      }
    }, 8000);
  }, [socket, socketConnected, capturedPhoto, description, userName, userSector, sent, faceDescriptor]);

  useEffect(() => {
    return () => {
      if (videoRef.current?.srcObject) {
        (videoRef.current.srcObject as MediaStream).getTracks().forEach(t => t.stop());
      }
    };
  }, []);

  if (sent) {
    return (
      <div className="px-4 py-12 text-center space-y-6">
        <div className={`w-20 h-20 rounded-full ${matchResult?.matched ? 'bg-red-500/20' : 'bg-green-500/20'} flex items-center justify-center mx-auto`}>
          {matchResult?.matched ? (
            <AlertTriangle className="w-10 h-10 text-red-400" />
          ) : (
            <CheckCircle2 className="w-10 h-10 text-green-400" />
          )}
        </div>
        <div>
          {matchResult?.matched ? (
            <>
              <h2 className="text-xl font-bold text-red-300">AGRESSOR IDENTIFICADO</h2>
              <p className="text-lg text-white mt-2 font-semibold">{matchResult.personName}</p>
              <p className="text-sm text-white/50 mt-1">Confiança: {matchResult.confidence?.toFixed(0)}%</p>
              <p className="text-xs text-red-300/70 mt-2">Equipe de segurança foi notificada automaticamente.</p>
            </>
          ) : (
            <>
              <h2 className="text-xl font-bold text-green-300">Relatório Enviado</h2>
              <p className="text-sm text-white/50 mt-2">
                {matchResult ? "Pessoa não encontrada no banco de dados. Foto salva para análise." : "A foto será analisada pela equipe de segurança."}
              </p>
            </>
          )}
        </div>
        <Button onClick={() => { setSent(false); setCapturedPhoto(null); setMatchResult(null); setFaceDescriptor(null); setScreen("home"); }} className="bg-purple-600 hover:bg-purple-700 cursor-pointer">
          Voltar ao Início
        </Button>
      </div>
    );
  }

  return (
    <div className="px-4 py-6 space-y-6">
      <div className="text-center">
        <FileWarning className="w-12 h-12 text-amber-400 mx-auto mb-3" />
        <h2 className="text-lg font-bold text-white">Reportar Possível Agressor</h2>
        <p className="text-xs text-white/50 mt-1">Tire uma foto discreta para enviar à equipe de segurança</p>
      </div>

      <div className="relative rounded-xl overflow-hidden border border-amber-500/20 bg-black" style={{ minHeight: "280px" }}>
        {capturedPhoto ? (
          <img src={capturedPhoto} alt="Captura" className="w-full h-auto object-contain" />
        ) : (
          <>
            <video ref={videoRef} className="w-full h-auto" playsInline muted />
            {!cameraActive && (
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <Camera className="w-12 h-12 text-white/20 mb-3" />
                <Button onClick={startCamera} className="bg-amber-600 hover:bg-amber-700 cursor-pointer">
                  <Camera className="w-4 h-4 mr-2" /> Abrir Câmera
                </Button>
              </div>
            )}
          </>
        )}
        <canvas ref={canvasRef} className="hidden" />
      </div>

      {cameraActive && !capturedPhoto && (
        <Button onClick={capturePhoto} disabled={detecting} className="w-full py-4 bg-amber-600 hover:bg-amber-700 cursor-pointer disabled:opacity-50">
          {detecting ? <Loader2 className="w-5 h-5 mr-2 animate-spin" /> : <Camera className="w-5 h-5 mr-2" />}
          {detecting ? "Analisando rosto..." : "Capturar Foto"}
        </Button>
      )}

      {capturedPhoto && (
        <>
          <div className="flex gap-2">
            <Button onClick={() => { setCapturedPhoto(null); startCamera(); }} variant="outline" className="flex-1 cursor-pointer text-xs">
              Tirar Outra
            </Button>
          </div>
          <div>
            <label className="text-xs text-white/40 mb-1 block">Descrição (opcional)</label>
            <textarea
              value={description}
              onChange={e => setDescription(e.target.value)}
              placeholder="Descreva a situação, aparência, comportamento..."
              className="w-full h-20 rounded-lg bg-white/5 border border-white/10 p-3 text-sm text-white placeholder:text-white/20 resize-none focus:outline-none focus:border-amber-500/40"
            />
          </div>
          <Button onClick={sendReport} disabled={sending} className="w-full py-4 bg-red-600 hover:bg-red-700 cursor-pointer">
            {sending ? <Loader2 className="w-5 h-5 animate-spin mr-2" /> : <Send className="w-5 h-5 mr-2" />}
            {sending ? "Enviando..." : "Enviar Relatório"}
          </Button>
        </>
      )}

      <p className="text-[10px] text-center text-white/30">
        Sua identidade será mantida em sigilo. A foto será usada apenas para fins de segurança.
      </p>
    </div>
  );
}

/* ─── SERVICES SCREEN ─── */
function ServicesScreen() {
  const openUber = () => {
    const uberUrl = "https://m.uber.com/ul/?action=setPickup&pickup=my_location";
    window.open(uberUrl, "_blank");
  };

  const openIFood = () => {
    const ifoodUrl = "https://www.ifood.com.br";
    window.open(ifoodUrl, "_blank");
  };

  const open99 = () => {
    const url99 = "https://99app.com";
    window.open(url99, "_blank");
  };

  return (
    <div className="px-4 py-6 space-y-6">
      <div className="text-center">
        <Car className="w-12 h-12 text-purple-400 mx-auto mb-3" />
        <h2 className="text-lg font-bold text-white">Serviços</h2>
        <p className="text-xs text-white/50 mt-1">Transporte e delivery disponíveis no estádio</p>
      </div>

      <div className="space-y-3">
        <ServiceCard
          icon="🚗"
          name="Uber"
          desc="Solicitar corrida para sua localização"
          color="from-gray-800 to-gray-900 border-white/10"
          onClick={openUber}
        />
        <ServiceCard
          icon="🏍️"
          name="99"
          desc="Corrida rápida com motorista próximo"
          color="from-yellow-900/20 to-yellow-900/10 border-yellow-500/20"
          onClick={open99}
        />
        <ServiceCard
          icon="🍔"
          name="iFood"
          desc="Peça comida e bebida no estádio"
          color="from-red-900/20 to-red-900/10 border-red-500/20"
          onClick={openIFood}
        />
      </div>

      {/* Ad space */}
      <div className="p-4 rounded-xl bg-gradient-to-r from-purple-900/20 via-indigo-900/20 to-purple-900/20 border border-purple-500/10 text-center">
        <p className="text-[10px] text-purple-300/40 uppercase tracking-widest mb-1">Parceiros</p>
        <p className="text-sm font-semibold text-white/30">Espaço para Parceiros Comerciais</p>
        <p className="text-[10px] text-white/20 mt-1">Restaurantes, lojas e serviços do estádio</p>
      </div>
    </div>
  );
}

function ServiceCard({ icon, name, desc, color, onClick }: {
  icon: string; name: string; desc: string; color: string; onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className={`w-full p-4 rounded-xl bg-gradient-to-r ${color} border flex items-center gap-4 cursor-pointer hover:scale-[1.01] transition-transform active:scale-[0.99]`}
    >
      <span className="text-3xl">{icon}</span>
      <div className="flex-1 text-left">
        <p className="text-sm font-semibold text-white/80">{name}</p>
        <p className="text-[10px] text-white/40">{desc}</p>
      </div>
      <ChevronRight className="w-5 h-5 text-white/30" />
    </button>
  );
}

/* ─── INFO SCREEN ─── */
function InfoScreen() {
  return (
    <div className="px-4 py-6 space-y-6">
      <div className="text-center">
        <Megaphone className="w-12 h-12 text-purple-400 mx-auto mb-3" />
        <h2 className="text-lg font-bold text-white">Informações do Estádio</h2>
      </div>

      <div className="space-y-4">
        <InfoCard title="MorumBIS" items={[
          "Capacidade: 66.795 lugares",
          "Endereço: Praça Roberto Gomes Pedrosa, 1 — Morumbi, SP",
          "Estacionamento: Portões 1, 2, 15 e 16",
        ]} />
        <InfoCard title="Segurança — ELLA by GAP" items={[
          "Monitoramento por IA em tempo real",
          "Reconhecimento facial ativo",
          "Agentes de segurança posicionados em todos os setores",
          "Em caso de emergência, use o botão SOS ou chacoalhe o celular",
        ]} />
        <InfoCard title="Dicas de Segurança" items={[
          "Mantenha seus pertences sempre à vista",
          "Em caso de tumulto, procure a saída mais próxima",
          "Não confronte agressores — acione a segurança",
          "Denúncias podem ser feitas de forma anônima pelo app",
        ]} />
        <InfoCard title="Contatos Úteis" items={[
          "Segurança do Estádio: Botão SOS no app",
          "SAMU: 192",
          "Polícia Militar: 190",
          "Bombeiros: 193",
        ]} />
      </div>

      {/* Ad space */}
      <div className="p-4 rounded-xl bg-gradient-to-r from-purple-900/20 via-indigo-900/20 to-purple-900/20 border border-purple-500/10 text-center">
        <p className="text-[10px] text-purple-300/40 uppercase tracking-widest mb-1">Patrocinador Oficial</p>
        <p className="text-sm font-semibold text-white/30">Espaço Publicitário Premium</p>
      </div>
    </div>
  );
}

function InfoCard({ title, items }: { title: string; items: string[] }) {
  return (
    <div className="p-4 rounded-xl bg-white/5 border border-white/10">
      <h3 className="text-sm font-semibold text-purple-300 mb-2">{title}</h3>
      <ul className="space-y-1.5">
        {items.map((item, i) => (
          <li key={i} className="flex items-start gap-2">
            <div className="w-1 h-1 rounded-full bg-purple-400/50 mt-1.5 shrink-0" />
            <span className="text-xs text-white/60">{item}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/* ─── MY EVENTS SCREEN ─── */
function MyEventsScreen({ events, setScreen }: { events: any[]; setScreen: (s: AppScreen) => void }) {
  return (
    <div className="px-4 py-6 space-y-6">
      <div className="text-center">
        <Bell className="w-12 h-12 text-purple-400 mx-auto mb-3" />
        <h2 className="text-lg font-bold text-white">Meus Chamados</h2>
        <p className="text-xs text-white/50 mt-1">Acompanhe o status dos seus acionamentos</p>
      </div>

      {events.length === 0 ? (
        <div className="text-center py-12">
          <Shield className="w-16 h-16 text-white/10 mx-auto mb-4" />
          <p className="text-sm text-white/30">Nenhum chamado registrado</p>
          <p className="text-xs text-white/20 mt-1">Seus acionamentos aparecerão aqui</p>
        </div>
      ) : (
        <div className="space-y-3">
          {events.map((evt, i) => (
            <div key={i} className="p-4 rounded-xl bg-white/5 border border-white/10">
              <div className="flex items-center justify-between mb-2">
                <Badge className={`text-[9px] ${evt.status === "resolved" ? "bg-green-500/20 text-green-400" : evt.status === "active" ? "bg-amber-500/20 text-amber-400" : "bg-blue-500/20 text-blue-400"}`}>
                  {evt.status === "resolved" ? "Resolvido" : evt.status === "active" ? "Em andamento" : "Pendente"}
                </Badge>
                <span className="text-[10px] text-white/30">{new Date(evt.createdAt).toLocaleString("pt-BR")}</span>
              </div>
              <p className="text-xs text-white/70">{evt.description || "Acionamento de emergência"}</p>
              {evt.sector && <p className="text-[10px] text-white/40 mt-1">Setor: {evt.sector}</p>}
              {evt.eventId && <p className="text-[10px] text-purple-400 mt-1">Evento #{evt.eventId}</p>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


/* ─── REGISTER AGGRESSOR SCREEN ─── */
function RegisterAggressorScreen({ setScreen }: {
  setScreen: (s: AppScreen) => void;
}) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [cameraActive, setCameraActive] = useState(false);
  const [capturedPhoto, setCapturedPhoto] = useState<string | null>(null);
  const [faceDescriptor, setFaceDescriptor] = useState<number[] | null>(null);
  const [fullName, setFullName] = useState("");
  const [identification, setIdentification] = useState("");
  const [address, setAddress] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [detecting, setDetecting] = useState(false);

  const createPerson = trpc.person.create.useMutation();

  const startCamera = useCallback(async () => {
    try {
      let stream: MediaStream;
      try {
        stream = await safeGetUserMedia({
          video: { facingMode: 'user', width: { ideal: 640 }, height: { ideal: 480 } },
        });
      } catch {
        stream = await safeGetUserMedia({ video: true });
      }
      const video = videoRef.current;
      if (video) {
        video.srcObject = null;
        video.removeAttribute('src');
        video.load();
        video.setAttribute('autoplay', '');
        video.setAttribute('playsinline', '');
        video.setAttribute('muted', '');
        video.setAttribute('webkit-playsinline', 'true');
        video.muted = true;
        video.playsInline = true;
        video.srcObject = stream;
        await new Promise<void>((resolve) => {
          const timeout = setTimeout(() => resolve(), 6000);
          const onPlaying = () => { clearTimeout(timeout); video.removeEventListener('playing', onPlaying); resolve(); };
          const onMeta = () => {
            video.removeEventListener('loadedmetadata', onMeta);
            video.play().then(() => { clearTimeout(timeout); resolve(); }).catch(() => {
              setTimeout(() => { video.play().catch(() => {}); clearTimeout(timeout); resolve(); }, 300);
            });
          };
          video.addEventListener('playing', onPlaying);
          video.addEventListener('loadedmetadata', onMeta);
          video.play().catch(() => {});
        });
        setCameraActive(true);
      }
    } catch {
      toast.error("Erro ao acessar câmera. Verifique as permissões.");
    }
  }, []);

  const captureAndDetect = useCallback(async () => {
    if (!videoRef.current || !canvasRef.current) return;
    setDetecting(true);
    try {
      // Capture photo
      const canvas = canvasRef.current;
      canvas.width = videoRef.current.videoWidth;
      canvas.height = videoRef.current.videoHeight;
      canvas.getContext("2d")?.drawImage(videoRef.current, 0, 0);
      const dataUrl = canvas.toDataURL("image/jpeg", 0.8);
      setCapturedPhoto(dataUrl);

      // Try face detection if face-api is loaded
      if (!faceapi) {
        try {
          faceapi = await import("@vladmandic/face-api");
          await faceapi.nets.tinyFaceDetector.loadFromUri("/api/face-models");
          await faceapi.nets.faceLandmark68TinyNet.loadFromUri("/api/face-models");
          await faceapi.nets.faceRecognitionNet.loadFromUri("/api/face-models");
        } catch {
          toast.info("Foto capturada. Detecção facial não disponível neste dispositivo.");
          setDetecting(false);
          // Stop camera
          (videoRef.current.srcObject as MediaStream)?.getTracks().forEach(t => t.stop());
          setCameraActive(false);
          return;
        }
      }

      const detection = await faceapi
        .detectSingleFace(videoRef.current, new faceapi.TinyFaceDetectorOptions({ inputSize: 320, scoreThreshold: 0.45 }))
        .withFaceLandmarks(true)
        .withFaceDescriptor();

      if (detection) {
        setFaceDescriptor(Array.from(detection.descriptor));
        toast.success("Rosto detectado e registrado para reconhecimento automático!");
      } else {
        toast.info("Foto capturada, mas nenhum rosto detectado. A foto será salva mesmo assim.");
      }

      // Stop camera
      (videoRef.current.srcObject as MediaStream)?.getTracks().forEach(t => t.stop());
      setCameraActive(false);
    } catch (err) {
      toast.error("Erro ao processar imagem");
    }
    setDetecting(false);
  }, []);

  const handleSave = useCallback(async () => {
    if (!fullName.trim()) {
      toast.error("Nome é obrigatório");
      return;
    }
    setSaving(true);
    try {
      await createPerson.mutateAsync({
        fullName: fullName.trim(),
        role: "agressor",
        identification: identification.trim() || undefined,
        photoBase64: capturedPhoto || undefined,
        notes: [
          notes.trim() ? notes.trim() : "",
          address.trim() ? `Endereço: ${address.trim()}` : "",
        ].filter(Boolean).join("\n") || undefined,
        status: "flagged",
        category: "restricted",
        faceDescriptor: faceDescriptor || undefined,
      });
      setSaved(true);
      toast.success("Agressor pré-cadastrado com sucesso! A IA irá identificá-lo automaticamente.");
    } catch (err) {
      toast.error("Erro ao salvar. Tente novamente.");
    }
    setSaving(false);
  }, [fullName, identification, address, notes, capturedPhoto, faceDescriptor, createPerson]);

  useEffect(() => {
    return () => {
      if (videoRef.current?.srcObject) {
        (videoRef.current.srcObject as MediaStream).getTracks().forEach(t => t.stop());
      }
    };
  }, []);

  if (saved) {
    return (
      <div className="px-4 py-12 text-center space-y-6">
        <div className="w-20 h-20 rounded-full bg-green-500/20 flex items-center justify-center mx-auto">
          <CheckCircle2 className="w-10 h-10 text-green-400" />
        </div>
        <div>
          <h2 className="text-xl font-bold text-green-300">Agressor Cadastrado</h2>
          <p className="text-sm text-white/50 mt-2">
            {fullName} foi registrado no banco de dados.
            {faceDescriptor ? " O reconhecimento facial irá identificá-lo automaticamente." : " Adicione uma foto com rosto visível para ativar o reconhecimento automático."}
          </p>
        </div>
        <div className="flex gap-3 justify-center">
          <Button onClick={() => { setSaved(false); setFullName(""); setIdentification(""); setAddress(""); setNotes(""); setCapturedPhoto(null); setFaceDescriptor(null); }} className="bg-purple-600 hover:bg-purple-700 cursor-pointer">
            Cadastrar Outro
          </Button>
          <Button onClick={() => setScreen("home")} variant="outline" className="cursor-pointer border-white/20 text-white/70">
            Voltar ao Início
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="px-4 py-6 space-y-5">
      <div className="text-center">
        <UserPlus className="w-12 h-12 text-red-400 mx-auto mb-3" />
        <h2 className="text-lg font-bold text-white">Pré-cadastrar Agressor</h2>
        <p className="text-xs text-white/50 mt-1">Registre dados do agressor para identificação automática pela IA</p>
      </div>

      {/* Name (required) */}
      <div>
        <label className="text-xs text-white/40 mb-1 block">Nome completo *</label>
        <input
          value={fullName}
          onChange={e => setFullName(e.target.value)}
          placeholder="Ex: João da Silva"
          className="w-full rounded-lg bg-white/5 border border-white/10 p-3 text-sm text-white placeholder:text-white/20 focus:outline-none focus:border-red-500/40"
        />
      </div>

      {/* Identification */}
      <div>
        <label className="text-xs text-white/40 mb-1 block">CPF / RG (opcional)</label>
        <input
          value={identification}
          onChange={e => setIdentification(e.target.value)}
          placeholder="Ex: 123.456.789-00"
          className="w-full rounded-lg bg-white/5 border border-white/10 p-3 text-sm text-white placeholder:text-white/20 focus:outline-none focus:border-red-500/40"
        />
      </div>

      {/* Address */}
      <div>
        <label className="text-xs text-white/40 mb-1 block flex items-center gap-1">
          <MapPinned className="w-3 h-3" /> Endereço(s) do agressor (opcional)
        </label>
        <textarea
          value={address}
          onChange={e => setAddress(e.target.value)}
          placeholder="Ex: Rua das Flores, 123 — Morumbi, SP&#10;Trabalho: Av. Paulista, 1000"
          className="w-full h-20 rounded-lg bg-white/5 border border-white/10 p-3 text-sm text-white placeholder:text-white/20 resize-none focus:outline-none focus:border-red-500/40"
        />
      </div>

      {/* Notes */}
      <div>
        <label className="text-xs text-white/40 mb-1 block">Observações (opcional)</label>
        <textarea
          value={notes}
          onChange={e => setNotes(e.target.value)}
          placeholder="Descrição física, comportamento, histórico..."
          className="w-full h-16 rounded-lg bg-white/5 border border-white/10 p-3 text-sm text-white placeholder:text-white/20 resize-none focus:outline-none focus:border-red-500/40"
        />
      </div>

      {/* Photo / Face capture */}
      <div>
        <label className="text-xs text-white/40 mb-2 block">Foto do agressor (para reconhecimento facial)</label>
        <div className="relative rounded-xl overflow-hidden border border-red-500/20 bg-black" style={{ minHeight: "240px" }}>
          {capturedPhoto ? (
            <div className="relative">
              <img src={capturedPhoto} alt="Captura" className="w-full h-auto object-contain" />
              {faceDescriptor && (
                <div className="absolute top-2 right-2 bg-green-500/80 rounded-full px-2 py-1 flex items-center gap-1">
                  <ScanFace className="w-3 h-3 text-white" />
                  <span className="text-[10px] text-white font-bold">Rosto detectado</span>
                </div>
              )}
            </div>
          ) : (
            <>
              <video ref={videoRef} className="w-full h-auto" playsInline muted />
              {!cameraActive && (
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                  <Camera className="w-12 h-12 text-white/20 mb-3" />
                  <Button onClick={startCamera} className="bg-red-600 hover:bg-red-700 cursor-pointer">
                    <Camera className="w-4 h-4 mr-2" /> Abrir Câmera
                  </Button>
                </div>
              )}
              {detecting && (
                <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                  <div className="text-center">
                    <Loader2 className="w-8 h-8 text-red-400 animate-spin mx-auto mb-2" />
                    <p className="text-xs text-white/70">Detectando rosto...</p>
                  </div>
                </div>
              )}
            </>
          )}
          <canvas ref={canvasRef} className="hidden" />
        </div>

        {cameraActive && !capturedPhoto && (
          <Button onClick={captureAndDetect} disabled={detecting} className="w-full mt-3 py-4 bg-red-600 hover:bg-red-700 cursor-pointer">
            <ScanFace className="w-5 h-5 mr-2" /> Capturar Foto e Detectar Rosto
          </Button>
        )}

        {capturedPhoto && (
          <Button onClick={() => { setCapturedPhoto(null); setFaceDescriptor(null); startCamera(); }} variant="outline" className="w-full mt-3 cursor-pointer text-xs border-white/20 text-white/70">
            Tirar Outra Foto
          </Button>
        )}
      </div>

      {/* Save button */}
      <Button
        onClick={handleSave}
        disabled={saving || !fullName.trim()}
        className="w-full py-5 text-base bg-red-600 hover:bg-red-700 text-white cursor-pointer disabled:opacity-50"
      >
        {saving ? <Loader2 className="w-5 h-5 animate-spin mr-2" /> : <UserPlus className="w-5 h-5 mr-2" />}
        {saving ? "Salvando..." : "Cadastrar Agressor"}
      </Button>

      <p className="text-[10px] text-center text-white/30">
        Os dados serão armazenados de forma segura e usados exclusivamente para fins de segurança.
        O reconhecimento facial identificará automaticamente esta pessoa nas câmeras.
      </p>
    </div>
  );
}


/* ─── PROTECTED TRIP SCREEN — FASE 3 ─── */
function TripScreen({ socket, socketConnected, userName, presenceId, sessionCode, setScreen }: {
  socket: any;
  socketConnected: boolean;
  userName: string;
  presenceId: string;
  sessionCode: string | null;
  setScreen: (screen: AppScreen) => void;
}) {
  const [destination, setDestination] = useState("");
  const [resolved, setResolved] = useState<{ address: string; lat: number; lng: number } | null>(null);
  const [tripActive, setTripActive] = useState(false);
  const [travelMode, setTravelMode] = useState<"walking" | "car">("walking");
  const [searching, setSearching] = useState(false);
  const watchIdRef = useRef<number | null>(null);
  const geoForward = trpc.geo.forward.useQuery({ address: destination || "destino" }, { enabled: false, retry: false });

  const stopWatch = useCallback(() => {
    if (watchIdRef.current !== null) {
      navigator.geolocation.clearWatch(watchIdRef.current);
      watchIdRef.current = null;
    }
  }, []);

  const searchDestination = async () => {
    if (destination.trim().length < 3) { toast.error("Digite o endereço de destino"); return; }
    setSearching(true);
    try {
      const result = await geoForward.refetch();
      if (result.data?.found && result.data.lat && result.data.lng) {
        setResolved({ address: result.data.address, lat: result.data.lat, lng: result.data.lng });
      } else {
        toast.error("Endereço não encontrado — inclua cidade e número");
      }
    } finally {
      setSearching(false);
    }
  };

  const startTrip = () => {
    const geo = getGeolocationOrNull();
    if (!resolved || !socket || !geo) { toast.error("GPS indisponível ou requer HTTPS neste aparelho"); return; }
    geo.getCurrentPosition((position) => {
      socket.emit("mobile:trip-start", {
        clientId: presenceId,
        sessionCode: sessionCode || undefined,
        reportedBy: userName || "Usuária App",
        destinationLabel: resolved.address,
        destLat: resolved.lat,
        destLng: resolved.lng,
        lat: position.coords.latitude,
        lng: position.coords.longitude,
        travelMode,
      });
      socket.once("mobile:trip-started-ack", (ack: { error?: boolean }) => {
        if (ack.error) { toast.error("Falha ao iniciar o acompanhamento"); return; }
        setTripActive(true);
        toast.success("Trajeto protegido ativo — a central está acompanhando");
        watchIdRef.current = geo.watchPosition(
          (point) => socket.emit("mobile:location-update", {
            clientId: presenceId,
            sessionCode: sessionCode || undefined,
            eventId: 0,
            lat: point.coords.latitude,
            lng: point.coords.longitude,
            accuracy: point.coords.accuracy,
            reportedBy: userName || "Usuário App",
          }),
          () => toast.error("Não foi possível atualizar a localização"),
          { enableHighAccuracy: true, maximumAge: 5000, timeout: 15000 },
        );
      });
    }, () => toast.error("Autorize o acesso à localização"), { enableHighAccuracy: true, timeout: 15000 });
  };

  const endTrip = () => {
    socket?.emit("mobile:trip-end", { clientId: presenceId });
    stopWatch();
    setTripActive(false);
    toast.success("Trajeto encerrado");
  };

  useEffect(() => () => stopWatch(), [stopWatch]);

  return <div className="px-4 py-6 space-y-4">
    <button onClick={() => setScreen("home")} className="flex items-center gap-1 text-xs text-white/50 cursor-pointer"><ArrowLeft className="w-3 h-3" /> Voltar</button>
    <div className="p-4 rounded-xl bg-gradient-to-br from-cyan-500/10 to-cyan-500/5 border border-cyan-500/20">
      <div className="flex items-center gap-3"><MapPinned className="w-6 h-6 text-cyan-400" /><h2 className="text-base font-bold text-cyan-300">Trajeto Protegido</h2></div>
      <p className="text-xs text-white/50 mt-2">Informe seu destino. A central acompanha seu GPS e é avisada em caso de desvio de rota ou parada prolongada.</p>
    </div>
    {!tripActive ? <div className="space-y-2">
      <input value={destination} onChange={(event) => { setDestination(event.target.value); setResolved(null); }} placeholder="Destino: rua, número e cidade" className="w-full px-3 py-3 rounded-lg bg-white/5 border border-white/10 text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-cyan-500/50" />
      <div className="grid grid-cols-2 gap-2">
        <Button type="button" variant={travelMode === "walking" ? "default" : "outline"} onClick={() => setTravelMode("walking")} className={travelMode === "walking" ? "bg-cyan-600 text-white" : "border-white/20 text-white/70"}>A pé</Button>
        <Button type="button" variant={travelMode === "car" ? "default" : "outline"} onClick={() => setTravelMode("car")} className={travelMode === "car" ? "bg-cyan-600 text-white" : "border-white/20 text-white/70"}>De carro</Button>
      </div>
      {!resolved ? <Button onClick={searchDestination} disabled={searching || !socketConnected} className="w-full bg-cyan-600 hover:bg-cyan-700 text-white">{searching ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <MapPin className="w-4 h-4 mr-2" />} Localizar destino</Button> : <>
        <div className="p-3 rounded-lg bg-green-500/10 border border-green-500/20 text-xs text-green-300">Destino confirmado: {resolved.address}</div>
        <Button onClick={startTrip} disabled={!socketConnected} className="w-full bg-green-600 hover:bg-green-700 text-white"><Shield className="w-4 h-4 mr-2" /> Iniciar acompanhamento</Button>
      </>}
    </div> : <div className="space-y-3">
      <div className="p-4 rounded-xl bg-green-500/10 border-2 border-green-500/30 text-center"><Shield className="w-8 h-8 text-green-400 mx-auto mb-2" /><p className="text-sm font-bold text-green-300">Trajeto ativo</p><p className="text-xs text-white/60 mt-1">Modo: {travelMode === "car" ? "carro" : "a pé"}. A central acompanha seu percurso em tempo real.</p></div>
      <Button onClick={endTrip} variant="outline" className="w-full border-white/20 text-white/70">Cheguei bem — encerrar acompanhamento</Button>
    </div>}
  </div>;
}
